import { loadStripe } from '@stripe/stripe-js';
import { supabase } from './supabase';
import toast from 'react-hot-toast';
import { products } from './stripe-config';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe('pk_test_TYooMQauvdEDq54NiTphI7jx');

export const createCheckoutSession = async (priceId: string, mode: 'subscription' | 'payment' = 'subscription') => {
  try {
    const toastId = toast.loading('Preparing checkout...');
    
    // Get the current user's JWT token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      toast.error('You must be logged in to subscribe', { id: toastId });
      return { success: false, error: 'Not authenticated' };
    }
    
    // Call our Supabase Edge Function to create a checkout session
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        price_id: priceId,
        success_url: `${window.location.origin}/settings?tab=subscription&success=true`,
        cancel_url: `${window.location.origin}/settings?tab=subscription&canceled=true`,
        mode,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      toast.error('Failed to create checkout session', { id: toastId });
      return { success: false, error: errorData.error || 'Failed to create checkout session' };
    }
    
    const { url } = await response.json();
    
    if (!url) {
      toast.error('No checkout URL returned', { id: toastId });
      return { success: false, error: 'No checkout URL returned' };
    }
    
    toast.success('Redirecting to checkout...', { id: toastId });
    
    // Redirect to Stripe Checkout
    window.location.href = url;
    
    return { success: true };
  } catch (error: any) {
    console.error('Error creating checkout session:', error);
    toast.error('Failed to create checkout session');
    return { success: false, error: error.message };
  }
};

export const getUserSubscription = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { subscription: null, error: 'Not authenticated' };
    }
    
    // Query the stripe_user_subscriptions view
    const { data, error } = await supabase
      .from('stripe_user_subscriptions')
      .select('*')
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching subscription:', error);
      return { subscription: null, error };
    }
    
    if (!data) {
      return { subscription: null };
    }
    
    // Find the product details based on price_id
    const product = products.find(p => p.priceId === data.price_id);
    
    return { 
      subscription: {
        ...data,
        product: product || null,
      }
    };
  } catch (error: any) {
    console.error('Error getting subscription:', error);
    return { subscription: null, error: error.message };
  }
};

export const getCustomerPortalUrl = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      toast.error('You must be logged in to manage your subscription');
      return { url: null, error: 'Not authenticated' };
    }
    
    // In a real implementation, this would call a serverless function
    // that creates a customer portal session
    
    // For demo purposes, we'll just return a mock URL
    return { url: 'https://billing.stripe.com/p/demo/customer_portal' };
  } catch (error: any) {
    console.error('Error getting customer portal URL:', error);
    return { url: null, error: error.message };
  }
};